import {AbsoluteCenter, Box, Center, Flex, useTheme, VStack} from '@chakra-ui/react';
import React, {useEffect, useState, useRef, useCallback} from 'react';
import ActiveRedemptionCard from '../Articles/Offers/ActiveRedemptionCard';
import {TextG12, TextG14} from '../typography';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Icon from '../Icon';
import {BlueButton, DarkWhiteButton} from '../button';
import {useRedemptionHistory} from '../../hooks/usePointsHistory';
import {SpinnerLoader} from '../Loader';

const ActiveRedemptionCards = ({hideNoCountdown = true}: {hideNoCountdown?: boolean}) => {
  const [searchParams] = useSearchParams();
  //const {activeRedemptions} = useActiveRedemptions();
  const {redemptionHistory, hasMore, fetchNextPage, isFetchingNextPage, isLoading} =
    useRedemptionHistory({
      isClaimed: false,
    });

  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedRedemptionId, setSelectedRedemptionId] = useState<string | null>(null);
  const lastCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const redemptionId = searchParams.get('redemptionId');
    setSelectedRedemptionId(redemptionId);
  }, [searchParams]);

  // Intersection Observer to detect when last card is visible
  useEffect(() => {
    if (!lastCardRef.current || !hasMore || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {threshold: 0.1}
    );

    observer.observe(lastCardRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetchingNextPage, fetchNextPage, redemptionHistory]);

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      color: theme.colors.lightBlue,
      fontSize: '2rem',
    }),
    [theme]
  );

  if (isLoading) {
    return <SpinnerLoader py={2} />;
  }
  if (!redemptionHistory || redemptionHistory.length <= 0)
    return (
      <AbsoluteCenter w="250px">
        <Flex alignItems="center" textAlign="center" flexDir="column" gap={4}>
          <Center>
            <Icon iconName="fi-rr-wallet" style={iconStyle} />
          </Center>
          <TextG14 color="#8E8E8E" fontWeight="500" lineHeight="20px" letterSpacing="-0.01em">
            Oops! Looks like you haven't chosen any perks.
          </TextG14>
          <TextG12 color="#8E8E8E" fontWeight="400" lineHeight="20px" letterSpacing="-0.01em">
            Let's check out what cool things you can get with your points right now.
          </TextG12>
          <BlueButton
            onClick={() =>
              navigate({
                pathname: '/search/offer',
              })
            }
            borderRadius="27.5px"
            px={6}
            py={3}
            w="auto"
            h="auto">
            Show Me The Perks
          </BlueButton>
        </Flex>
      </AbsoluteCenter>
    );

  // Filter redemptions with offers and find the last valid one
  const validRedemptions = redemptionHistory.filter((r) => r.offer);
  const lastValidIndex = validRedemptions.length - 1;

  return (
    <VStack my={4} spacing={4} width="100%">
      {validRedemptions.map((redemption, i) => {
        const isLastCard = i === lastValidIndex;

        return (
          <Box key={redemption.id || i} ref={isLastCard ? lastCardRef : null} width="100%">
            <ActiveRedemptionCard
              offer={redemption.offer}
              activeRedemption={redemption}
              hideCard={false}
              hideNoCountdown={hideNoCountdown}
              isSelected={redemption.offer.id?.toString() === selectedRedemptionId}
            />
          </Box>
        );
      })}
      {isFetchingNextPage && <SpinnerLoader />}
    </VStack>
  );
};

export default ActiveRedemptionCards;
